import * as React from "react";
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HomeIcon from "@mui/icons-material/Home";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import { useApolloClient, useMutation } from "@apollo/client";
import { LOGOUT_USER } from "../graphql/gqlUser";
import { UserContext } from "../contexts/userContext";
import SettingsIcon from "@mui/icons-material/Settings";
import { useDarkmode } from "../hooks/useDarkmode";
import { CorporateFare } from "@mui/icons-material";
import { T } from "./T";
import LoginIcon from "@mui/icons-material/Login";

const stringToColor = (string: string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).slice(-2);
  }
  return color;
};

const contrastColor = (color: string) => {
  const r = parseInt(color.substr(1, 2), 16);
  const g = parseInt(color.substr(3, 2), 16);
  const b = parseInt(color.substr(5, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155 ? "#000000" : "#ffffff";
};

export const UserMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { isDarkmode, toggleDarkmode } = useDarkmode();

  const client = useApolloClient();
  const userContext = React.useContext(UserContext);
  const [logoutUser] = useMutation(LOGOUT_USER, {
    onCompleted: () => {
      client.clearStore();
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const accountName = userContext.user.firstName;
  const avatarColor = stringToColor(accountName);
  const avatarTextColor = contrastColor(avatarColor);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexGrow: 0,
        gap: "1rem",
      }}
    >
      <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
        <Avatar sx={{ bgcolor: avatarColor, color: avatarTextColor }}>
          {accountName.charAt(0)}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            mt: 1.5,
            minWidth: 160,
            bgcolor: "background.paper",
            color: "text.primary",
            "& .MuiMenuItem-root": {
              fontSize: "1rem",
            },
          },
        }}
      >
        {userContext.user.id && (
          <MenuItem component={Link} to={`/${userContext.user.id}/projects`}>
            <HomeIcon sx={{ mr: 1 }} />
            <T str="Projects" />
          </MenuItem>
        )}
        {userContext.user.id && (
          <MenuItem component={Link} to={`/${userContext.user.id}/polesets`}>
            <EqualizerIcon sx={{ mr: 1 }} />
            <T str="Polesets" />
          </MenuItem>
        )}

        {userContext.user.id && (
          <MenuItem component={Link} to={`/settings`}>
            <SettingsIcon sx={{ mr: 1 }} />
            <T str="Settings" />
          </MenuItem>
        )}

        <MenuItem component={Link} to={`/faq`}>
          <HelpIcon sx={{ mr: 1 }} />
          <T str="Faq" />
        </MenuItem>

        {userContext.user.isAdmin && (
          <MenuItem component={Link} to={`/admin-center`}>
            <CorporateFare sx={{ mr: 1 }} /> <T str="Admin center" />
          </MenuItem>
        )}

        <Divider />

        {userContext.user.id && (
          <MenuItem onClick={toggleDarkmode}>
            {isDarkmode ? (
              <>
                <LightModeIcon sx={{ mr: 1 }} /> <T str="Light mode" />
              </>
            ) : (
              <>
                <DarkModeIcon sx={{ mr: 1 }} /> <T str="Dark mode" />
              </>
            )}
          </MenuItem>
        )}
        {userContext.user.id ? (
          <MenuItem
            onClick={async () => {
              await logoutUser();
              userContext.refetch();
            }}
          >
            <CloseIcon sx={{ mr: 1 }} /> <T str="Log out" />
          </MenuItem>
        ) : (
          <MenuItem component={Link} to={`/login`}>
            <LoginIcon sx={{ mr: 1 }} /> <T str="Log in" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
